import { default as React } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import ListSubheader from '@mui/material/ListSubheader';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import { DocsHelpIcon } from './components';

const useStyles = makeStyles(theme => ({
  formControl: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  helperText: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },
  domainPicker: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

/** Sentinel value that identifies the "Other (Not a Solution)" option */
const OTHER_INTERNAL_TOOLS_VALUE = 'other-internal-tools';
/** Prefix for freeSolo custom system values in the Select */
const FREESOLO_PREFIX = '__freesolo__:';

interface SolutionOption {
  value: string; // entity reference like "system:default/learning-center"
  label: string; // display name like "WellSky Learning Center"
}

interface DomainOption {
  value: string; // domain name like "enterprise-solutions"
  label: string; // display name like "Enterprise Solutions"
}

const SolutionPickerField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  const classes = useStyles();
  const theme = useTheme();
  const catalogApi = useApi(catalogApiRef);
  const [solutions, setSolutions] = useState<SolutionOption[]>([]);
  const [domains, setDomains] = useState<DomainOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInternalToolsMode, setIsInternalToolsMode] = useState(
    () => !!formData?.startsWith('other-internal-tools/'),
  );
  const [selectedDomain, setSelectedDomain] = useState<DomainOption | null>(
    null,
  );
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [searchText, setSearchText] = useState('');

  const fieldLabel = schema?.title || 'Solution';
  const docsUrl = uiSchema?.['ui:options']?.docsUrl as string | undefined;

  // Derive Select value from formData.
  // Use empty string while loading to prevent MUI Select from receiving a
  // controlled value that has no matching MenuItem yet (would cause a crash).
  const getSelectValue = () => {
    if (loading) return '';
    if (isInternalToolsMode) return OTHER_INTERNAL_TOOLS_VALUE;
    return formData || '';
  };
  const selectValue = getSelectValue();

  const formatEntityName = (name: string): string => {
    return name
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [systemResult, domainResult] = await Promise.all([
          catalogApi.getEntities({ filter: { kind: 'System' } }),
          catalogApi.getEntities({ filter: { kind: 'Domain' } }),
        ]);

        // Build system options (exclude engineering-led systems)
        const systemOptions: SolutionOption[] = systemResult.items
          .map((entity: Entity) => {
            const name = entity.metadata.name;
            const title =
              entity.metadata.title || formatEntityName(name) || name;
            return {
              value: `system:default/${name}`,
              label: title,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        setSolutions(systemOptions);

        // Build domain options from Domain entities
        const domainOptions: DomainOption[] = domainResult.items
          .map((entity: Entity) => {
            const name = entity.metadata.name;
            const title =
              entity.metadata.title || formatEntityName(name) || name;
            return { value: name, label: title };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        setDomains(domainOptions);

        // Restore formData selection on initial load
        if (formData) {
          if (formData.startsWith('other-internal-tools/')) {
            const domainName = formData.split('/').pop() || '';
            const matchedDomain = domainOptions.find(
              d => d.value === domainName,
            );
            setIsInternalToolsMode(true);
            if (matchedDomain) {
              setSelectedDomain(matchedDomain);
            }
          } else {
            setIsInternalToolsMode(false);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching solutions or domains:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, refreshTrigger]);

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    if (value === OTHER_INTERNAL_TOOLS_VALUE) {
      setIsInternalToolsMode(true);
      setSelectedDomain(null);
      onChange('');
    } else if (value.startsWith(FREESOLO_PREFIX)) {
      const customName = value.slice(FREESOLO_PREFIX.length);
      setIsInternalToolsMode(false);
      setSelectedDomain(null);
      onChange(customName);
    } else {
      setIsInternalToolsMode(false);
      setSelectedDomain(null);
      onChange(value);
    }
  };

  const handleDomainChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    const matched = domains.find(d => d.value === value);
    setSelectedDomain(matched || null);
    if (value) {
      onChange(`other-internal-tools/${value}`);
    } else {
      onChange('');
    }
  };

  const filteredSolutions = useMemo(() => {
    if (!searchText) return solutions;
    const lower = searchText.toLowerCase();
    return solutions.filter(s => s.label.toLowerCase().includes(lower));
  }, [solutions, searchText]);

  // Show freeSolo "Use: ..." option when typing >= 3 chars with no exact match
  const showFreeSoloOption = useMemo(() => {
    if (searchText.length < 3) return false;
    const lower = searchText.toLowerCase();
    return !solutions.some(
      s =>
        s.label.toLowerCase() === lower ||
        s.value.toLowerCase().includes(lower),
    );
  }, [solutions, searchText]);

  const hasError = !!(rawErrors && rawErrors.length > 0);

  return (
    <Box>
      <FormControl
        className={classes.formControl}
        error={hasError}
        fullWidth
        variant="outlined"
      >
        <Box className={classes.labelContainer}>
          <InputLabel shrink sx={{ position: 'relative', transform: 'none' }}>
            {fieldLabel}
            {required ? ' *' : ' (Optional)'}
          </InputLabel>
          {docsUrl && (
            <DocsHelpIcon
              docsUrl={docsUrl}
              tooltipText="Solution/System documentation"
            />
          )}
          {loading ? (
            <CircularProgress size={18} sx={{ ml: 1 }} />
          ) : (
            <Tooltip title="Refresh solutions list from catalog">
              <IconButton
                size="small"
                onClick={handleRefresh}
                disabled={loading}
                aria-label="refresh solutions"
                sx={{ ml: 0.5 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Select
          value={selectValue}
          onClose={() => setSearchText('')}
          onChange={handleChange}
          required={required}
          displayEmpty
          MenuProps={{
            autoFocus: false,
            PaperProps: { sx: { maxHeight: 350 } },
          }}
          renderValue={value => {
            if (!value) {
              return (
                <span style={{ color: theme.palette.text.disabled }}>
                  Select a solution from the list
                </span>
              );
            }
            if (value === OTHER_INTERNAL_TOOLS_VALUE) {
              return 'Other (Not a Solution)';
            }
            const matched = solutions.find(s => s.value === value);
            return matched ? matched.label : String(value);
          }}
        >
          <ListSubheader
            sx={{
              backgroundColor: theme.palette.background.paper,
              pt: 1,
              pb: 1,
            }}
          >
            <TextField
              size="small"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              placeholder="Type to filter or create new..."
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyDown={e => e.stopPropagation()}
            />
          </ListSubheader>
          <MenuItem value={OTHER_INTERNAL_TOOLS_VALUE}>
            Other (Not a Solution)
          </MenuItem>
          <Divider />
          {showFreeSoloOption && (
            <MenuItem
              value={`${FREESOLO_PREFIX}${searchText}`}
              sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
            >
              <Box>
                <Typography variant="body1" fontWeight={500}>
                  Use: &ldquo;{searchText}&rdquo;
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Create new system
                </Typography>
              </Box>
            </MenuItem>
          )}
          {filteredSolutions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
          {filteredSolutions.length === 0 &&
            !showFreeSoloOption &&
            searchText && <MenuItem disabled>No matches found</MenuItem>}
        </Select>

        {hasError && <FormHelperText>{rawErrors?.[0]}</FormHelperText>}
        {!hasError && (
          <FormHelperText className={classes.helperText}>
            The business solution or product this repository belongs to.
          </FormHelperText>
        )}
        <FormHelperText className={classes.helperText}>
          Don&apos;t see your solution? Select &ldquo;Other (Not a
          Solution)&rdquo; from the dropdown above, then select your Business
          Unit.
        </FormHelperText>
      </FormControl>

      {/* Business Unit picker — shown only when "Other (Not a Solution)" is selected */}
      {isInternalToolsMode && (
        <FormControl
          className={classes.domainPicker}
          fullWidth
          variant="outlined"
        >
          <Box className={classes.labelContainer}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none' }}>
              Business Unit *
            </InputLabel>
          </Box>
          <Select
            value={selectedDomain?.value || ''}
            onChange={handleDomainChange}
            required
            displayEmpty
            renderValue={value => {
              if (!value) {
                return (
                  <span style={{ color: theme.palette.text.disabled }}>
                    Select the business unit
                  </span>
                );
              }
              const matched = domains.find(d => d.value === value);
              return matched ? matched.label : String(value);
            }}
          >
            {domains.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            The business unit that owns this internal tool. Stored as{' '}
            <code>spec.domain</code> in the catalog.
          </FormHelperText>
        </FormControl>
      )}
    </Box>
  );
};

export const SolutionPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'SolutionPicker',
    component: SolutionPickerField,
  }),
);
